import {
  convert,
  Instant,
  LocalDate,
  LocalDateTime,
  nativeJs,
  ZonedDateTime,
  ZoneId,
  ZoneOffset,
} from '@js-joda/core'

// -- Default format options --------------------------------------------------

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
}

const DATETIME_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
}

// -- Formatting (js-joda → localized string) ---------------------------------

export function formatDate(
  value: LocalDate,
  locale?: string | string[],
  options: Intl.DateTimeFormatOptions = DATE_OPTIONS,
): string {
  return Intl.DateTimeFormat(locale, options).format(convert(value).toDate())
}

export function formatDateTime(
  value: LocalDateTime,
  locale?: string | string[],
  options: Intl.DateTimeFormatOptions = DATETIME_OPTIONS,
): string {
  return Intl.DateTimeFormat(locale, options).format(convert(value).toDate())
}

export function formatZonedDateTime(
  value: ZonedDateTime,
  locale?: string | string[],
  options: Intl.DateTimeFormatOptions = DATETIME_OPTIONS,
): string {
  return Intl.DateTimeFormat(locale, options).format(convert(value).toDate())
}

export function formatInstant(
  value: Instant,
  locale?: string | string[],
  options: Intl.DateTimeFormatOptions = DATETIME_OPTIONS,
  zone: ZoneId = ZoneId.SYSTEM,
): string {
  return formatZonedDateTime(ZonedDateTime.ofInstant(value, zone), locale, options)
}

// -- Parsing (ISO-8601 string → js-joda) -------------------------------------

export function parseIsoDate(value: string): LocalDate {
  return LocalDate.parse(value)
}

export function parseIsoDateTime(value: string): LocalDateTime {
  return LocalDateTime.parse(value)
}

export function parseIsoZonedDateTime(value: string): ZonedDateTime {
  return ZonedDateTime.parse(value)
}

export function instantFromEpochMillis(
  value: number,
  zone: ZoneId = ZoneOffset.UTC,
): ZonedDateTime {
  return ZonedDateTime.ofInstant(Instant.ofEpochMilli(value), zone)
}

export function instantFromEpochSeconds(
  value: number,
  zone: ZoneId = ZoneOffset.UTC,
): ZonedDateTime {
  return ZonedDateTime.ofInstant(Instant.ofEpochSecond(value), zone)
}

// -- Conversion (js-joda ↔ native Date) --------------------------------------

export function toNativeDate(value: LocalDate | LocalDateTime | ZonedDateTime): Date {
  return convert(value).toDate()
}

export function fromNativeDate(value: Date): LocalDate {
  return LocalDate.from(nativeJs(value))
}

export function fromNativeDateTime(value: Date): LocalDateTime {
  return LocalDateTime.from(nativeJs(value))
}

export function fromNativeZonedDateTime(value: Date): ZonedDateTime {
  return ZonedDateTime.from(nativeJs(value))
}
